import React, { useEffect, useRef, useState } from "react";
import {
  CardProduct,
  CardProductName,
  ProductInfo,
  Row,
  Price,
  Icons,
  StyledInfinityScroll,
  Text,
  IconContainer,
  ClickContainer,
  CardProductImage,
} from "./MarketplacePage.styles";

import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UseAnimations from "react-useanimations";
import heart from "react-useanimations/lib/heart";
import {
  getFavoriteMarketplaceItemsAll,
  toggleFavoriteMarketplaceItem,
} from "../../../store/slices/marketplace/asyncThunks";
import {
  resetAddItem,
  selectFavoriteItems,
  selectFavoriteItemsData,
} from "../../../store/slices/marketplace/marketplaceSlice";

import { selectBusinessUserInfo } from "../../../store/slices/businessUser/slice";
import { priceFormatter } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import Footer from "../../../components/Footer.js";
import { getCurrencySymbol } from "../../../utils/constants";
import { t } from "i18next";
import SkeletonMarketplaceLoader from "../../../components/SkeletonLoader/SkeletonMarketplaceLoader/SkeletonMarketplaceLoader.js";
import { useGetCategoriesQuery } from "../../../store/slices/marketplace/marketplaceApi.js";
import openToast from "../../../utils/toast.js";
import {
  selectedLocale,
  selectSidebarOpen,
} from "../../../store/slices/common/slice.js";
import { useSwipeable } from "react-swipeable";
import AppLoader from "../../../components/AppLoader.js";
import LoginModal from "../../../components/Modal/LoginModal.js";

function ProductCard({ product, reverse, itemDetails }) {
  const dispatch = useDispatch();
  const toastId = useRef(null);
  const locale = useSelector(selectedLocale);
  const ar = locale === "ar";
  const [activeIndex, setActiveIndex] = useState(0);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setActiveIndex((prevIndex) =>
        prevIndex === product?.photos.length - 1 ? 0 : prevIndex + 1,
      );
    },
    onSwipedRight: () => {
      setActiveIndex((prevIndex) =>
        prevIndex === 0 ? product?.photos.length - 1 : prevIndex - 1,
      );
    },
  });

  return (
    <CardProduct {...handlers}>
      <CardProductImage
        $imageUrl={product?.photos[activeIndex]?.url}
        $loaded={true}
      />
      <Icons>
        <IconContainer>
          <UseAnimations
            animation={heart}
            fillColor="#484888"
            reverse={reverse}
            strokeColor="#484888"
            size={32}
            onClick={() => {
              dispatch(
                toggleFavoriteMarketplaceItem({
                  marketplaceItemId: product.id,
                }),
              )
                .unwrap()
                .catch((err) =>
                  openToast(
                    toastId,
                    "marketplace-fav-item",
                    "error",
                    t(err.message),
                  ),
                );
            }}
          />
        </IconContainer>
      </Icons>
      <ClickContainer onClick={itemDetails} />

      <ProductInfo>
        <CardProductName
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {ar && product?.nameAr ? product?.nameAr : product?.name}
        </CardProductName>
        <Row>
          <Price>
            {`${t("from")}
            ${priceFormatter(product?.price ?? product?.samplePrice, true, 15)}
            ${getCurrencySymbol(product?.currency)}`}
          </Price>
          {product.minQuantity && (
            <Price>
              Min Qty:{" "}
              <Price style={{ fontWeight: 800 }}>{product.minQuantity}</Price>
            </Price>
          )}
        </Row>
      </ProductInfo>
    </CardProduct>
  );
}

const FavoritePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const items = useSelector(selectFavoriteItemsData);
  const favData = useSelector(selectFavoriteItems);
  const { loggedIn, role } = useSelector(selectBusinessUserInfo);
  const [selectedCategory, setSelectedCategory] = useState(-1);

  useEffect(() => {
    dispatch(resetAddItem());
  }, [dispatch]);

  const { data: categories, isFetching: categoriesPending } =
    useGetCategoriesQuery({ search: "" });

  useEffect(() => {
    dispatch(
      getFavoriteMarketplaceItemsAll({
        page: 1,
        sortBy: "createdAt",
        order: "DESC",
        search: "",
        category: (selectedCategory ?? "") !== -1 ? selectedCategory : "",
      }),
    );
  }, [dispatch, selectedCategory]);

  const loadMoreItems = () => {
    dispatch(
      getFavoriteMarketplaceItemsAll({
        page: favData.page + 1,
        sortBy: "createdAt",
        order: "DESC",
        search: "",
        category: (selectedCategory ?? "") !== -1 ? selectedCategory : "",
      }),
    );
  };
  const handleItemClick = (itemId) => {
    navigate(`/marketplace/${itemId}`);
  };

  const opened = useSelector(selectSidebarOpen);
  const locale = useSelector(selectedLocale);
  const ar = locale === "ar";

  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollUpY, setLastScrollUpY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(null);
  const [loginModal, setLoginModal] = useState(false);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      const lastScrollTop = lastScrollTopRef.current;

      if (currentScrollTop === 0) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
        setScrollDirection(null);
        lastScrollTopRef.current = currentScrollTop;
        return;
      }

      if (currentScrollTop > lastScrollTop) {
        setScrollDirection("down");
        setLastScrollUpY(currentScrollTop);
      } else {
        setScrollDirection("up");
      }

      if (scrollDirection === "up" && currentScrollTop < lastScrollUpY - 350) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
      }

      if (
        scrollDirection === "down" &&
        currentScrollTop > lastScrollUpY + 350
      ) {
        setHeaderVisible(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    const handleScrollDebounced = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener("scroll", handleScrollDebounced);

    return () => {
      window.removeEventListener("scroll", handleScrollDebounced);
    };
  }, [scrollDirection]);

  return (
    <>
      {favData.loading ? (
        <SkeletonMarketplaceLoader />
      ) : (
        <>
          {!favData.loading && items?.length <= 0 && (
            <Text>{t("no-items-in-favorites")}</Text>
          )}
          {items?.length > 0 && (
            <StyledInfinityScroll
              $admin={role === "superAdmin"}
              dataLength={items.length}
              hasMore={!favData.maxReached}
              next={loadMoreItems}
              loader={<SkeletonMarketplaceLoader single />}
            >
              {items?.map((product) => (
                <ProductCard
                  key={product.id}
                  itemDetails={() => {
                    handleItemClick(product.id);
                  }}
                  product={product}
                  reverse={true}
                />
              ))}
            </StyledInfinityScroll>
          )}
        </>
      )}

      {!loggedIn && <Footer />}
      <LoginModal open={loginModal} close={() => setLoginModal(false)} />
    </>
  );
};

export default FavoritePage;
